import React, { useEffect, useState } from 'react';
import {
  BoxStyled,
  GridStyled,
  BMRSection,
  BMRTitle,
  BMRValue,
  TypographyStyledHeader,
  SubInfoStyled,
  TypographyStyled
} from './style';
import { setPaediatricEnergy } from '../../../store/slices/metaDataSlice';
import { useDispatch } from 'react-redux';
import infoIcon from '../../../assets/icons/infoIcon.svg';
import graphIcon from '../../../assets/icons/graphIcon.svg';
import exerciseIcon from '../../../assets/icons/exerciseIcon.svg';

import { PlaceholderGrid, CutOffIcon } from '../../../styles/globalStyles';
import { Button, Container, Modal, Box } from '@mui/material';
import { useAppSelector } from '../../../store/hooks';
import { metaDataSelector } from '../../../store/globalSelectors';
import { NavigationButtons } from '../../../shared/components/navigationButtons';
import { AddOnInfoModal } from './addOnInfoModal';
import { DoubleTextBlueCardStyled } from '../../../shared/components/doubleTextBlueCardStyled';
import { InfoModal } from '../../../shared/components/modal/InfoModal';
import { energyConstants } from '../../../shared/shared.constants';
import {
  isBetween,
  calcYearsOfAge,
  calcMonthsOfAge
} from '../../../shared/utils/utilFunctions';
import { TabPanel } from './TabPanel';

export const PaediatricEnergy = () => {
  const {
    patientInformation,
    estimatingRequirements: { paediatricEnergy }
  } = useAppSelector(metaDataSelector);
  const dispatch = useDispatch();
  const { energyRequirement } = paediatricEnergy;
  const [openModal1, setOpenModal1] = useState(false);
  const handleOpenModal1 = () => setOpenModal1(true);
  const handleCloseModal1 = () => setOpenModal1(false);
  const [openModal2, setOpenModal2] = useState(false);
  const handleOpenModal2 = () => setOpenModal2(true);
  const handleCloseModal2 = () => setOpenModal2(false);
  const weight =
    patientInformation.metric.estimatedDryWeight ||
    patientInformation.metric.weight;
  const { age, gender } = patientInformation;
  const calculatedAgeInYears = calcYearsOfAge(age.years, age.months);
  const calculatedAgeInMonths = calcMonthsOfAge(age.years, age.months);
  let childType;
  let subTitle = '';
  if (isBetween(calculatedAgeInMonths, 7, 11)) {
    // 7 month to 11months
    childType = 'infant';
  } else if (isBetween(calculatedAgeInMonths, 12, 47)) {
    // 1 year to 3 years 11months
    childType = 'toddler';
    subTitle = '(for 1-3 years old)';
  } else if (isBetween(calculatedAgeInMonths, 48, 119)) {
    // 4 years to 9 years 11months
    childType = 'kid';
    subTitle = '(for 4-9 years old)';
  } else if (isBetween(calculatedAgeInMonths, 120, 215)) {
    // 10 years to 17 years 11months
    childType = 'teen';
    subTitle = '(for 10-17 years old)';
  } else childType = 'infant<7months';

  useEffect(() => {
    let efaVal = 0,
      efaKgVal = 0,
      efwVal = 0,
      efwKgVal = 0;
    const genderVal = gender.toLowerCase();
    if (childType === 'infant' && calculatedAgeInMonths > 6) {
      energyConstants.infant[genderVal].forEach((e) => {
        if (e.ageInMonths === calculatedAgeInMonths) {
          efaVal = e.energy;
          efaKgVal = e.energyForAge;
          efwVal = +(e.energyForAge * weight);
          efwKgVal = e.energyForAge;
        }
      });
    } else if (!childType.includes('infant')) {
      energyConstants[childType][genderVal].forEach((e) => {
        if (
          isBetween(calculatedAgeInMonths, e.minAgeInMonths, e.maxAgeInMonths)
        ) {
          efaVal = e.ree;
          efwVal = e.ree;
          efwKgVal = Math.round(e.ree / weight);
        }
      });
    }
    if (
      childType != paediatricEnergy.childType ||
      efaVal != energyRequirement.energyForAge ||
      efaKgVal != energyRequirement.energyForAgePerKg ||
      efwVal != energyRequirement.energyForWeight ||
      efwKgVal != energyRequirement.energyForWeightPerKg
    ) {
      dispatch(
        setPaediatricEnergy({
          ...paediatricEnergy,
          childType: childType,
          tabIndex: 0,
          activityLevel: 'REE',
          energyRequirement: {
            ...paediatricEnergy.energyRequirement,
            energyForAge: efaVal,
            energyForAgePerKg: efaKgVal,
            energyForWeight: efwVal,
            energyForWeightPerKg: efwKgVal
          }
        })
      );
    }
  }, [weight, calculatedAgeInMonths, gender]);

  return (
    <>
      <BoxStyled>
        <Container
          sx={{
            display: 'flex',
            flexDirection: 'row',
            padding: '0 !important'
          }}
        >
          <TypographyStyledHeader>EFSA REQUIREMENTS</TypographyStyledHeader>
          <Button
            onClick={() => handleOpenModal1()}
            sx={{ height: '1.25rem', minWidth: '1.25rem', padding: 0 }}
          >
            <img src={infoIcon} alt={'infoIcon'} />
          </Button>
        </Container>
        <GridStyled container className="width-100">
          <BMRSection>
            <BMRTitle variant="h2">{'Weight (kg):'}</BMRTitle>
            <BMRValue data-testid="weight">{weight}</BMRValue>
          </BMRSection>
          <BMRSection sx={{ marginTop: 0 }}>
            <BMRTitle variant="h2">
              {calculatedAgeInMonths > 11 ? 'Age (years):' : 'Age (months):'}
            </BMRTitle>
            <BMRValue>
              {calculatedAgeInMonths > 11
                ? calculatedAgeInYears
                : calculatedAgeInMonths}
            </BMRValue>
          </BMRSection>
        </GridStyled>

        {!childType.includes('infant') && (
          <CutOffIcon>
            <img src={exerciseIcon} alt="waterDropIcon" />
          </CutOffIcon>
        )}

        <PlaceholderGrid container width={'100%'}>
          {!childType.includes('infant') && (
            <Box
              sx={{
                width: '100%',
                textAlign: 'center',
                mb: 2
              }}
            >
              <TypographyStyled variant="h3">
                CHOOSE ACTIVITY LEVEL
              </TypographyStyled>
              <SubInfoStyled sx={{ paddingBottom: '1.25rem' }}>
                {subTitle}
              </SubInfoStyled>
              <TabPanel />
            </Box>
          )}

          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              mb: 2,
              mt: 3,
              width: '100%',
              position: 'relative'
            }}
          >
            {childType.includes('infant') ? (
              <DoubleTextBlueCardStyled
                title="REQUIREMENTS FOR AGE"
                heading1=""
                label1={
                  energyRequirement.energyForAge === 0
                    ? 'NA'
                    : `${energyRequirement.energyForAge} kcal/day`
                }
                label2={
                  energyRequirement.energyForAgePerKg === 0
                    ? 'NA'
                    : `${energyRequirement.energyForAgePerKg} kcal/kg/day`
                }
                icon={graphIcon}
              />
            ) : (
              <DoubleTextBlueCardStyled
                title="REQUIREMENTS FOR AGE"
                heading1=""
                label1={`${energyRequirement.energyForAge} kcal/day`}
                icon={graphIcon}
              />
            )}
          </Box>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              mb: 2,
              mt: 5,
              width: '100%',
              position: 'relative'
            }}
          >
            <Button
              onClick={() => handleOpenModal2()}
              sx={{
                height: '1.25rem',
                minWidth: '1.25rem',
                padding: 0,
                position: 'absolute',
                top: '1.25rem',
                right: '1.25rem'
              }}
            >
              <img src={infoIcon} alt={'infoIcon1'} />
            </Button>
            <DoubleTextBlueCardStyled
              title="REQUIREMENTS FOR WEIGHT"
              heading1=""
              label1={
                energyRequirement.energyForWeight === 0
                  ? 'NA'
                  : `${energyRequirement.energyForWeight} kcal/day`
              }
              label2={
                energyRequirement.energyForWeightPerKg === 0
                  ? 'NA'
                  : `${energyRequirement.energyForWeightPerKg} kcal/kg/day`
              }
              icon={graphIcon}
            />
          </Box>
        </PlaceholderGrid>
      </BoxStyled>
      <NavigationButtons
        nextDisabled={false}
        nextRoute={'/estimating-requirements/paediatric-protein'}
      />
      <Modal
        open={openModal1}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <AddOnInfoModal onClose={handleCloseModal1} />
      </Modal>
      <InfoModal
        open={openModal2}
        onClose={handleCloseModal2}
        title={` `}
        content={`Requirements based on weight calculated using kcal/day from EFSA (2013) requirements and the weight of the patient.`}
      />
    </>
  );
};
